import React, { useState, useEffect, useRef, useCallback } from 'react';
import { onForegroundMessage } from '../firebase';
import './ChatModal.css';
import { apiUrl } from '../api';

function ChatModal({ isOpen, onClose, productId, receiverId, receiverUsername, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const token = localStorage.getItem('token');
  const currentUserIdInt = parseInt(currentUserId, 10);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const query = productId ? `?productId=${productId}` : '';
      //console.log('Fetching messages for receiverId:', receiverId, 'productId:', productId);
      const response = await fetch(apiUrl(`/api/messages/conversation/${receiverId}${query}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const responseText = await response.text();
      //console.log('Fetch messages raw response:', responseText);

      if (response.ok) {
        try {
          const data = JSON.parse(responseText);
          console.log('Fetched messages:', data);
          setMessages(data);
        } catch (parseError) {
          console.error('Failed to parse messages response:', parseError);
        }
      } else {
        console.error('Error fetching messages - Status:', response.status);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [productId, receiverId, token]);

  useEffect(() => {
    if (isOpen && receiverId) {
      fetchMessages();
      //const interval = setInterval(fetchMessages, 2000);
      //return () => clearInterval(interval);
    }
  }, [isOpen, receiverId, fetchMessages]);

  // Listen for FCM push notifications
useEffect(() => {
  const unsubscribe = onForegroundMessage((payload) => {
    console.log("Push received:", payload);
    console.log("Push payload data:", payload.data);

    if (payload.data) {
      const newMessage = {
        id: parseInt(payload.data.id, 10),              // normalize to number
        sender_id: parseInt(payload.data.sender_id, 10),
        receiver_id: parseInt(payload.data.receiver_id, 10),
        message_text: payload.data.message_text,
        created_at: payload.data.created_at             // ISO string from backend
      };

      setMessages((prev) => [...prev, newMessage]);
     } 
    // else {
    //   // Fallback: refresh from backend
    //   fetchMessages();
    // }
  });

  return () => unsubscribe && unsubscribe();
}, []);


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        receiver_id: parseInt(receiverId),
        product_id: productId ? parseInt(productId) : null,
        message_text: messageText.trim()
      };

      // console.log('Sending message with payload:', payload);
      // console.log('Current User ID:', currentUserId);
      console.log('Token:', token ? 'Present' : 'Missing');

      const response = await fetch(apiUrl('/api/messages'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      // console.log('Raw response:', responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON. Raw response:', responseText);
        setError('Server error: ' + responseText.substring(0, 100));
        setLoading(false);
        return;
      }

      // console.log('Message send response:', responseData);

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to send message');
      }

      setMessageText('');
      fetchMessages(); // Fetch messages immediately after sending
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="chat-modal-overlay" onClick={onClose}>
      <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="chat-header">
          <h2>Chat with {receiverUsername}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="chat-messages">
          {messages.length === 0 ? (
            <p className="no-messages">No messages yet. Start the conversation!</p>
          ) : (
            messages.map((msg) => {
              const isSentByCurrentUser = parseInt(msg.sender_id, 10) === currentUserIdInt;

              return (
                <div
                  key={msg.id}
                  className={`message ${isSentByCurrentUser ? 'sent' : 'received'}`}
                >
                  <div className="message-bubble">
                    <p className="message-text">{msg.message_text}</p>
                    <span className="message-time">
                      {new Date(msg.created_at).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSendMessage} className="chat-input-form">
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type your message..."
            className="message-input"
            disabled={loading}
          />
          <button
            type="submit"
            className="send-btn"
            disabled={loading || !messageText.trim()}
          >
            {loading ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatModal;
